/**
 * Ouvre une URL hors PWA (navigateur système / nouvel onglet Chrome-Safari).
 * `target=_blank` depuis une iframe sandboxed recrée souvent une 2ᵉ fenêtre PWA.
 */

export type MailBodyLinkKind = "external" | "protocol" | "ignore";

export function classifyMailBodyHref(href: string): MailBodyLinkKind {
  const h = href.trim();
  if (!h || h.startsWith("#")) return "ignore";
  const lower = h.toLowerCase();
  if (lower.startsWith("javascript:") || lower.startsWith("data:")) return "ignore";
  if (
    lower.startsWith("mailto:") ||
    lower.startsWith("tel:") ||
    lower.startsWith("sms:") ||
    lower.startsWith("geo:")
  ) {
    return "protocol";
  }
  return "external";
}

/** Résout href relatif (base = document iframe ou page). */
export function resolveMailBodyHref(href: string, baseHref?: string): string | null {
  const raw = href.trim();
  if (!raw) return null;
  try {
    return new URL(
      raw,
      baseHref || (typeof window !== "undefined" ? window.location.href : undefined)
    ).href;
  } catch {
    return null;
  }
}

function clickTopLevelBlankAnchor(absolute: string): void {
  const anchor = document.createElement("a");
  anchor.href = absolute;
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  anchor.referrerPolicy = "no-referrer";
  // Hors flux — certains WebView ignorent un nœud détaché.
  anchor.style.cssText = "position:fixed;left:-9999px;top:0;opacity:0;pointer-events:none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/**
 * Force l’ouverture dans le navigateur (hors display-mode standalone).
 * Ancre synthétique dans le document **top** — pas window.open (2ᵉ PWA iOS/Android).
 */
export function openUrlInSystemBrowser(url: string): void {
  if (typeof document === "undefined") return;
  const absolute = resolveMailBodyHref(url);
  if (!absolute) return;

  const kind = classifyMailBodyHref(absolute);
  if (kind === "ignore") return;

  if (kind === "protocol") {
    window.location.href = absolute;
    return;
  }

  // Toujours depuis le document top (hors iframe mail).
  // En PWA : surtout pas window.open (2ᵉ fenêtre standalone).
  clickTopLevelBlankAnchor(absolute);
}

/** Clics http(s) dans le corps mail → navigateur système (pas une 2ᵉ PWA). */
export function bindMailBodyExternalLinks(doc: Document): () => void {
  const onActivate = (event: Event) => {
    if (event instanceof MouseEvent && event.button !== 0 && event.button !== 1) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest("a[href], area[href]");
    if (!link) return;

    const href = link.getAttribute("href");
    if (href == null) return;

    const absolute = resolveMailBodyHref(href, doc.baseURI || undefined);
    if (!absolute) return;

    const kind = classifyMailBodyHref(absolute);
    if (kind === "ignore") return;

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === "function") {
      event.stopImmediatePropagation();
    }

    openUrlInSystemBrowser(absolute);
  };

  doc.addEventListener("click", onActivate, true);
  doc.addEventListener("auxclick", onActivate, true);

  return () => {
    doc.removeEventListener("click", onActivate, true);
    doc.removeEventListener("auxclick", onActivate, true);
  };
}
